import React, { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import NuevaFirma from './pages/NuevaFirma';
import Pendientes from './pages/Pendientes';
import ConfigConsentimientos from './pages/ConfigConsentimientos';
import Usuarios from './pages/Usuarios';
import Firmados from './pages/Firmados';
import DatosCentro from './pages/DatosCentro';
import PaymentResult from './pages/PaymentResult';
import Estadisticas from './pages/Estadisticas';
import PatientPortal from './pages/PatientPortal';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminEmpresas from './pages/admin/AdminEmpresas';
import AdminCrearEmpresa from './pages/admin/AdminCrearEmpresa';
import AdminMemberships from './pages/admin/AdminMemberships';
import AdminMembershipConfig from './pages/admin/AdminMembershipConfig';
import AdminRenewals from './pages/admin/AdminRenewals';
import AdminEmpresaDetalle from './pages/admin/AdminEmpresaDetalle';
import AdminMembershipDetalle from './pages/admin/AdminMembershipDetalle';
import AdminMediosPago from './pages/admin/AdminMediosPago';
import { AdminLayout } from './components/admin/AdminLayout';
import { Toaster } from './components/ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AppContext = React.createContext();

// Module-level (not component-level) so it survives React StrictMode's
// mount→cleanup→mount simulation, which creates a fresh component instance — a
// useRef-based guard would just reset with it. Also enforces a short cooldown so two
// back-to-back mounts don't each fire their own request even once the first has settled.
let inFlightMembershipFetch = null;
let lastMembershipFetchAt = 0;
const MEMBERSHIP_FETCH_COOLDOWN_MS = 1000;

function PrivateRoute({ children }) {
  const token = localStorage.getItem('konemed_token');
  return token ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const token = localStorage.getItem('konemed_admin_token');
  return token ? <AdminLayout>{children}</AdminLayout> : <Navigate to="/admin/login" />;
}

function App() {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  // Membership status is fetched once per session here and shared via context, instead
  // of every clinic page (and DashboardLayout) independently re-fetching it on every
  // navigation. That duplication was also what made the "membership paused" screen flash
  // incorrectly during page transitions and occasionally get stuck showing after a
  // transient/rate-limited request failure — see [[feedback_konemed_design_consistency]].
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);

  const refetchMembership = useCallback((force = false) => {
    const token = localStorage.getItem('konemed_token');
    if (!token) {
      setMembershipLoading(false);
      return Promise.resolve();
    }
    if (inFlightMembershipFetch) {
      return inFlightMembershipFetch;
    }
    if (!force && Date.now() - lastMembershipFetchAt < MEMBERSHIP_FETCH_COOLDOWN_MS) {
      setMembershipLoading(false);
      return Promise.resolve();
    }
    const promise = axios
      .get(`${API_URL}/company/membership-status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        setMembershipStatus(res.data);
      })
      .catch((error) => {
        // Transient/rate-limited failures should not be treated as "membership blocked" —
        // keep whatever the last known-good status was instead of resetting it.
        console.error('Error fetching membership status:', error);
      })
      .finally(() => {
        setMembershipLoading(false);
        lastMembershipFetchAt = Date.now();
        inFlightMembershipFetch = null;
      });
    inFlightMembershipFetch = promise;
    return promise;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('konemed_token');
    const userData = localStorage.getItem('konemed_user');
    const companyData = localStorage.getItem('konemed_company');

    if (token && userData && companyData) {
      setUser(JSON.parse(userData));
      setCompany(JSON.parse(companyData));
      refetchMembership();
    } else {
      setMembershipLoading(false);
    }
  }, [refetchMembership]);

  const contextValue = {
    user,
    setUser,
    company,
    setCompany,
    API_URL,
    membershipStatus,
    membershipLoading,
    refetchMembership
  };

  return (
    <AppContext.Provider value={contextValue}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/firmar/:token" element={<PatientPortal />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/empresas" element={<AdminRoute><AdminEmpresas /></AdminRoute>} />
            <Route path="/admin/crear-empresa" element={<AdminRoute><AdminCrearEmpresa /></AdminRoute>} />
            <Route path="/admin/empresa/:companyId" element={<AdminRoute><AdminEmpresaDetalle /></AdminRoute>} />
            <Route path="/admin/memberships" element={<AdminRoute><AdminMemberships /></AdminRoute>} />
            <Route path="/admin/membership/:companyId" element={<AdminRoute><AdminMembershipDetalle /></AdminRoute>} />
            <Route path="/admin/membership-config" element={<AdminRoute><AdminMembershipConfig /></AdminRoute>} />
            <Route path="/admin/renewals" element={<AdminRoute><AdminRenewals /></AdminRoute>} />
            <Route path="/admin/medios-pago" element={<AdminRoute><AdminMediosPago /></AdminRoute>} />
            
            {/* User Routes */}
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/nueva-firma" element={<PrivateRoute><NuevaFirma /></PrivateRoute>} />
            <Route path="/pendientes" element={<PrivateRoute><Pendientes /></PrivateRoute>} />
            <Route path="/configuracion-consentimientos" element={<PrivateRoute><ConfigConsentimientos /></PrivateRoute>} />
            <Route path="/usuarios" element={<PrivateRoute><Usuarios /></PrivateRoute>} />
            <Route path="/firmados" element={<PrivateRoute><Firmados /></PrivateRoute>} />
            <Route path="/datos-centro" element={<PrivateRoute><DatosCentro /></PrivateRoute>} />
            <Route path="/payment-result" element={<PrivateRoute><PaymentResult /></PrivateRoute>} />
            <Route path="/estadisticas" element={<PrivateRoute><Estadisticas /></PrivateRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AppContext.Provider>
  );
}

export default App;
